import { useCallback, useEffect, useMemo, useRef, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccessType } from "../../../common/enums/DRCXEnums";
import SaveAsDialog, { bindSaveAsDialog, useSaveAsDialogState } from "../../../components/save-as-dialog/SaveAsDialog";
import StepMenu, { LandingDefinition, StepDefinition } from "../../../components/step-menu/StepMenu";
import { StepMenuEditModeEnum, useStepMenuState } from "../../../components/step-menu/StepMenuState";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useObjectTagValuesConfiguration } from "../../../queries/object-tag-values/UseObjectTagValuesConfiguration";
import { useSaveObjectTagValuesConfiguration } from "../../../queries/object-tag-values/UseSaveObjectTagValuesConfiguration";
import i18n from "../../../services/localization-service/i18n";
import { ObjectTagValuesConfiguration, ObjectTagValuesTypeEnum } from "../../../services/object-tag-values-data-service/ConfigurationModels";
import Form from "../../forms/form/Form";
import UnsavedChangesPrompt from "../../unsaved-changes-prompt/UnsavedChangesPrompt";
import { useObjectTagValuesConfigurationEditorState } from "./ObjectTagValuesConfigurationEditorState";
import {
    allTagGroupsValid,
    //arrayFieldIsValid,
    configurationFieldIsDirty,
    mergeConfigurations,
    selectedTagsIsValid,
    //tagGroupFieldIsValid,
    toConfigurationField
} from "./ObjectTagValuesConfigurationEditorUtils";
import {
    ObjectTagValuesConfigurationFieldType,
    ObjectTagValuesGridColumnConfigurationFieldType,
    ObjectTagValuesTagGroupConfigurationFieldType,
    TagGroupFieldKeyType
} from "./ObjectTagValuesConfigurationFieldTypes";
import DisplayOptionsStep from "./display-options-step/DisplayOptionsStep";
import Landing from "./landing/Landing";
import SelectTagsStep from "./select-tags-step/SelectTagsStep";

export enum StepEnum {
    Landing = 0,
    SelectTags = 1,
    DisplayOptions = 2
}

export const NewConfigurationID = -1;

export type ObjectTagValuesConfigurationEditorCloseType = {
    reason: "cancel" | "saveAsNew" | "saveChanges";
    configurationID: number | undefined;
};

export type ObjectTagValuesConfigurationEditorProps = {
    configurationID?: number;
    duplicate?: boolean;
    onClose: ({ reason, configurationID }: ObjectTagValuesConfigurationEditorCloseType) => void;
};

export default function ObjectTagValuesConfigurationEditor({
    configurationID,
    duplicate,
    onClose,
}: ObjectTagValuesConfigurationEditorProps): JSX.Element {

    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const saveAsDialog = useSaveAsDialogState();
    const tagGroupBackup = useRef<ObjectTagValuesTagGroupConfigurationFieldType | undefined>(undefined);

    const {
        state: {
            tagGroupKey
        },
        selectTagGroup
    } = useObjectTagValuesConfigurationEditorState();

    const initValue: ObjectTagValuesConfigurationFieldType = {
        id: -1,
        name: "",
        access: AccessType.Private,
        tagGroups: new Array<ObjectTagValuesTagGroupConfigurationFieldType>(),
        gridColumns: new Array<ObjectTagValuesGridColumnConfigurationFieldType>()
    };

    const form = useForm<ObjectTagValuesConfigurationFieldType>({
        mode: "onChange", // all?
        defaultValues: initValue
    });

    const {
        getValues,
        formState: {
            dirtyFields
        },
        reset,
        setValue,
        control,
        handleSubmit
    } = form;

    const editMode = duplicate ||
        !configurationID ||
        configurationID === NewConfigurationID
        ? StepMenuEditModeEnum.Create
        : StepMenuEditModeEnum.Edit;

    const configuration = useWatch({ control: control }) as ObjectTagValuesConfigurationFieldType;
    const tagGroup = tagGroupKey
        ? getValues(tagGroupKey)
        : undefined;

    const isSelectTagsValid = selectedTagsIsValid(tagGroup);
    const isDisplayOptionsValid = isSelectTagsValid;

    const isSelectTagsEnabled = !!tagGroup;
    const isDisplayOptionsEnabled = isSelectTagsValid;
    const isTagGroupsEnabled = isDisplayOptionsEnabled;

    const landingDefinition: LandingDefinition = useMemo(() => {
        return {
            key: StepEnum.Landing,
            // title: i18n.t("objectTagValuesConfiguration.steps.landing.title"),
            title: "Tag Groups",
            disabled: !isTagGroupsEnabled
        };
    }, [isTagGroupsEnabled]);

    const stepDefinitions: Array<StepDefinition> = useMemo(() => {

        return [{
            key: StepEnum.SelectTags,
            title: i18n.t("objectTagValuesConfiguration.steps.selectTags.title"),
            disabled: !isSelectTagsEnabled,
            valid: isSelectTagsValid
        }, {
            key: StepEnum.DisplayOptions,
            title: i18n.t("objectTagValuesConfiguration.steps.displayOptions.title"),
            disabled: !isDisplayOptionsEnabled,
            valid: isDisplayOptionsValid
        }];
    }, [
        isSelectTagsValid,
        isDisplayOptionsValid,
        isSelectTagsEnabled,
        isDisplayOptionsEnabled
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveAsNewButtonState,
            saveChangesButtonState
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveChangesButtonDisabled,
        setPreviewButtonDisabled
    } = useStepMenuState(
        editMode,
        landingDefinition,
        stepDefinitions
    );

    const { data: configurationModel } = useQueryNotifications(useObjectTagValuesConfiguration)({
        groupID: routeContext.groupID as number,
        configurationID: configurationID ?? -1,
        // need to determine this from object type        
        viewType: ObjectTagValuesTypeEnum.Device
    });

    const { mutateAsync: saveConfiguration } = useQueryNotifications(useSaveObjectTagValuesConfiguration)({
        groupID: routeContext.groupID!,
        objectType: routeContext.contextObject?.objectType!,
        objectID: routeContext.contextObject?.id!,
        onSuccess: (data) => {
            // save success            
            acknowledge(t("configuration.saveSuccessMessage", {
                configurationType: t("configuration.types.report"),
                configurationName: data.name
            }));
        }
    });

    // A separate isDirty from the one returned by useForm is implemented
    // here so we can exclude the expanded and formID fields from the data.
    const isDirty = configurationFieldIsDirty(dirtyFields);

    const updateButtonStates = useCallback(() => {

        const isAllTagGroupsValid = allTagGroupsValid(configuration.tagGroups);

        setPreviewButtonDisabled(!isAllTagGroupsValid);

        switch (activeStep) {
            case StepEnum.Landing:

                setSaveAsNewButtonDisabled(!isAllTagGroupsValid);

                if (editMode) {
                    setSaveChangesButtonDisabled(!(isAllTagGroupsValid && isDirty));
                }

                break;

            case StepEnum.SelectTags:
                setNextButtonDisabled(!isDisplayOptionsEnabled);
                break;

            case StepEnum.DisplayOptions:
                setNextButtonDisabled(!isTagGroupsEnabled);
                break;
        }
    }, [
        editMode,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveChangesButtonDisabled,
        setPreviewButtonDisabled,
        activeStep,
        isDirty,
        isDisplayOptionsEnabled,
        isTagGroupsEnabled,
        configuration.tagGroups
    ]);

    // #region Effects

    // This effect updates the form values after data is retrieved. Want to keep this
    // simple with minimal dependencies, otherwise rendering problems may ensue!
    useEffect(() => {
        if (configurationModel) {
            // We have our configuration now, so we need to convert it to
            // values for react hook form.
            const configuration: ObjectTagValuesConfigurationFieldType = toConfigurationField(duplicate, configurationModel);

            // Reset the form with the configuration's values
            reset(configuration);
        }
    }, [
        configurationModel,
        reset,
        duplicate,
    ]);

    // This effect updates step menu button states whenever the configuration form values change
    useEffect(() => {

        updateButtonStates();
    }, [configuration, updateButtonStates]);

    // #endregion Effects

    // #region Event Handlers

    async function onCancelEdit() {

        if (!isDirty || (await getUserExitConfirmation())) {
            onClose({ reason: "cancel", configurationID: undefined });
        }
    }

    function onEditTagGroup(tagGroupKey: TagGroupFieldKeyType) {

        tagGroupBackup.current = { ...getValues(tagGroupKey) };

        selectTagGroup(tagGroupKey);
        setActiveStep(StepEnum.SelectTags);
    }

    function onCancelStep() {
        if (tagGroupKey && tagGroupBackup.current) {
            setValue(tagGroupKey, tagGroupBackup.current);
        }

        setActiveStep(StepEnum.Landing);
        selectTagGroup(undefined);
    }

    function onStepChange(fromStep: number, toStep: number) {
        setActiveStep(toStep);
        if (toStep === StepEnum.Landing) {
            // if (tagGroupKey) {
            //     setValue(`${tagGroupKey}.expanded`, false);
            // }
            selectTagGroup(undefined);
        }
    }

    async function onSaveAsNew() {
        const configuration = getValues();

        const result = await saveAsDialog.open({
            name: configuration.name,
            configurationType: t("configuration.types.objectTagValues"),
            forceEnable: duplicate
        });

        if (result.reason === "ok") {

            const savedConfiguration = await save({
                ...configuration,
                id: NewConfigurationID,
                name: result.values?.name as string,
                // access: AccessType.Private
            });

            onClose({
                reason: "saveAsNew",
                configurationID: savedConfiguration?.id
            });
        }
    }

    async function onSaveChanges() {

        const configuration = getValues();

        const savedConfiguration = await save(configuration);

        onClose({
            reason: "saveChanges",
            configurationID: savedConfiguration?.id
        });
    }


    // #endregion Event Handlers

    async function save(configuration: ObjectTagValuesConfigurationFieldType): Promise<ObjectTagValuesConfiguration | undefined> 
    {

        if (configurationModel && configuration) {
            // Merge all of our changes with the original configuration
            const model = mergeConfigurations(configurationModel, configuration);

            // Save it and return the saved configuration with updated ID, if applicable
            return await saveConfiguration(model);
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    return (
        <>
            <Form form={form} sx={{ padding: 0 }}>
                <StepMenu
                    sxSteps={{ overflowY: activeStep === StepEnum.Landing ? 'auto' : undefined }}
                    landing={landing}
                    steps={steps}
                    title={
                        activeStep === StepEnum.Landing
                            ? configuration.name ?? "New View"
                            : tagGroup?.name ?? "New Tag Group"
                    }
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveAsNewButtonState={saveAsNewButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {activeStep === StepEnum.Landing &&
                        <Landing onEditTagGroup={onEditTagGroup} />
                    }

                    {activeStep === StepEnum.SelectTags && tagGroupKey &&
                        <SelectTagsStep tagGroupKey={tagGroupKey} />
                    }

                    {activeStep === StepEnum.DisplayOptions && tagGroupKey &&
                        <DisplayOptionsStep tagGroupKey={tagGroupKey} />
                    }
                </StepMenu>
            </Form>

            <SaveAsDialog {...bindSaveAsDialog(saveAsDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </>
    );
}
